import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { IBudget, IBudgetVersionService } from "../../../../models/budgetV2";
import { fetchBudgetV2 } from "../../../../api/fetchBudgetV2";
import { ITruckerPayPlanData } from "../../../../models/truckerPayPlan";
import { fetchContractDetails } from "../../../../api/fetchContractDetails";
import { Asset } from "../../../../models/asset";
import { BudgetStatus } from "../../enums/budgetStatus";
import { formatDateTimeWithTimezone } from "../../../../utils/dateUtils";
import { getStatusColor, getStatusLabel } from "../../utils/statusHelpers";
import {
  createEmptyBudgetVersionService,
  createEmptyBudgetVersionServiceItem,
} from "../utils/emptyBudgetVersionService";
import { getComparableBudgetSnapshot } from "../utils/buildBudgetRequestFromBudget";
import { useBudgetEditActions, IUseBudgetEditActionsOverrides } from "./useBudgetEditActions";
import { useBudgetAttachments } from "../../hooks/useBudgetAttachments";

export const isClientApprovedStatus = (status?: string): boolean => {
    if (!status) return false;
    return (
        status === BudgetStatus.APPROVED ||
        status === getStatusLabel(BudgetStatus.APPROVED) ||
        status === "Aprovado pelo gestor"
    );
};

const getApprovedDateLabel = (budget: IBudget | null): string => {
    const status =
        budget?.versionData?.statusLabel ??
        budget?.statusLabel ??
        budget?.currentUserStatus;

    if (!isClientApprovedStatus(status)) return "-";

    const counterProposalAcceptedAt = budget?.versionData?.counterProposalApprovedAt;
    if (counterProposalAcceptedAt) {
        return formatDateTimeWithTimezone(counterProposalAcceptedAt);
    }

    const acceptedAt = budget?.versionData?.updatedAt ?? budget?.updatedAt;
    if (acceptedAt) {
        return formatDateTimeWithTimezone(acceptedAt);
    }

    return "-";
};

export interface IUseBudgetEditOverrides extends IUseBudgetEditActionsOverrides {
    budgetId?: string;
    version?: number;
}

export const useBudgetEdit = (persona: string, overrides?: IUseBudgetEditOverrides) => {
    const params = useParams<{ budgetId: string; version: string }>();
    const budgetId = overrides?.budgetId ?? params.budgetId;
    const version = overrides?.version !== undefined ? String(overrides.version) : params.version;

    const budgetVersion = useMemo(() => {
        const parsed = Number(version);
        return Number.isFinite(parsed) ? parsed : undefined;
    }, [version]);

    const [budget, setBudget] = useState<IBudget | null>(null);
    const [originalBudget, setOriginalBudget] = useState<IBudget | null>(null);
    // For each current service, the index it had in the loaded budget (undefined = added during edit).
    // Services from the API may come without `id`, so they can't be matched by it.
    const [serviceOrigins, setServiceOrigins] = useState<(number | undefined)[]>([]);
    const [truckerPayPlan, setTruckerPayPlan] = useState<ITruckerPayPlanData | null>(null);
    const [loading, setLoading] = useState(true);

    const {
        attachments,
        addAttachments,
        removeAttachment,
        uploadPendingAttachments,
    } = useBudgetAttachments(budgetId);

    const {
        submitting,
        saveBudgetUpdate,
        acceptBudget,
        rejectBudget,
        counterProposeBudget,
    } = useBudgetEditActions(budget, budgetVersion, uploadPendingAttachments, {
        onSaved: overrides?.onSaved,
        onDecided: overrides?.onDecided,
    }, originalBudget);

    useEffect(() => {
        if (!budgetId || budgetVersion === undefined) {
            setLoading(false);
            return;
        }

        const fetchBudget = async () => {
            setLoading(true);
            try {
                const budgetResponse = await fetchBudgetV2(budgetId, budgetVersion);
                setBudget(budgetResponse);
                setOriginalBudget(structuredClone(budgetResponse));
                setServiceOrigins((budgetResponse?.versionData?.services ?? []).map((_, index) => index));
            } finally {
                setLoading(false);
            }
        };

        fetchBudget();
    }, [budgetId, budgetVersion]);

    useEffect(() => {
        // the budget carries the vehicle snapshot; without a chassis the route becomes /assets//trucker-pay-plan
        if (!budget?.vehicleChassis) return;

        const fetchTruckerPayPlan = async () => {
            const truckerPayPlanResponse = await fetchContractDetails({ chassis: budget.vehicleChassis || '', odometer: budget.vehicleOdometer || 0, hourmeter: budget.vehicleHourMeter || 0 } as Asset);
            if (truckerPayPlanResponse) setTruckerPayPlan(truckerPayPlanResponse);
        };
        fetchTruckerPayPlan();
    }, [budget?.vehicleChassis, budget?.vehicleOdometer, budget?.vehicleHourMeter]);

    const showCoverageBalanceBox = (): boolean => {
        const budgetHasProductWithoutCoverage = (budget?.versionData?.services ?? []).some(service => (service.serviceItems ?? []).some(item => !item.isCovered));
        return budgetHasProductWithoutCoverage && persona === 'fleet-manager' && !!truckerPayPlan && budget?.currentUserStatus !== 'APPROVED';
    }

    const setServices = (services: IBudgetVersionService[]) => {
        setBudget(prevBudget => prevBudget ? { ...prevBudget, versionData: { ...prevBudget.versionData, services } } : null);
    };

    const removeService = (serviceIndex: number) => {
        setServiceOrigins((prev) => prev.filter((_, i) => i !== serviceIndex));
        setBudget((prevBudget) => prevBudget
            ? { ...prevBudget, versionData: { ...prevBudget.versionData, services: (prevBudget.versionData?.services ?? []).filter((_, i) => i !== serviceIndex) } }
            : null);
    };

    const getInitialService = (serviceIndex: number): IBudgetVersionService | undefined => {
        const originIndex = serviceOrigins[serviceIndex];
        return originIndex === undefined ? undefined : originalBudget?.versionData?.services?.[originIndex];
    };

    const addEmptyService = () => {
        setServiceOrigins((prev) => [...prev, undefined]);
        setBudget((prevBudget) => {
            if (!prevBudget) return null;
            const currentServices = prevBudget.versionData?.services ?? [];
            return {
                ...prevBudget,
                versionData: {
                    ...prevBudget.versionData,
                    services: [...currentServices, createEmptyBudgetVersionService()],
                },
            };
        });
    };

    const addEmptyItemToService = (serviceIndex: number) => {
        setBudget((prevBudget) => {
            if (!prevBudget) return null;
            const currentServices = prevBudget.versionData?.services ?? [];
            const services = currentServices.map((service, index) => {
                if (index !== serviceIndex) return service;
                const currentItems = service.items ?? service.serviceItems ?? [];
                const nextRef = `${serviceIndex + 1}.${String(currentItems.length + 1).padStart(2, "0")}`;
                const emptyItem = createEmptyBudgetVersionServiceItem(nextRef);
                const nextItems = [...currentItems, emptyItem];
                return {
                    ...service,
                    items: nextItems,
                    serviceItems: nextItems,
                };
            });
            return {
                ...prevBudget,
                versionData: {
                    ...prevBudget.versionData,
                    services,
                },
            };
        });
    };

    const statusLabel = budget?.versionData?.statusLabel ?? budget?.statusLabel ?? "";
    const statusBadgeClass = getStatusColor(statusLabel);
    const approvedDateLabel = useMemo(() => getApprovedDateLabel(budget), [budget]);

    const hasPendingAttachments = attachments.some((attachment) => Boolean(attachment.file));
    const hasBudgetChanges = useMemo(() => {
        const currentSnapshot = getComparableBudgetSnapshot(budget);
        const originalSnapshot = getComparableBudgetSnapshot(originalBudget);
        if (!currentSnapshot || !originalSnapshot) return hasPendingAttachments;
        return JSON.stringify(currentSnapshot) !== JSON.stringify(originalSnapshot) || hasPendingAttachments;
    }, [budget, originalBudget, hasPendingAttachments]);

    return {
        budget,
        truckerPayPlan,
        loading,
        submitting,
        statusLabel,
        statusBadgeClass,
        approvedDateLabel,
        hasBudgetChanges,

        setBudget,
        showCoverageBalanceBox,
        setServices,
        removeService,
        getInitialService,
        addEmptyService,
        addEmptyItemToService,
        saveBudgetUpdate,
        acceptBudget,
        rejectBudget,
        counterProposeBudget,
        attachments,
        addAttachments,
        removeAttachment,
    };
};
